import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { usePageTitle } from "@/lib/content";
import { Reveal } from "@/components/site/Reveal";
import { getGuide, GUIDES } from "@/lib/guides";

const CANON_BASE = "https://sitebound.com.au";

function JsonLd({ data }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => el.remove();
  }, [data]);
  return null;
}

function Callout({ title, text, testid }) {
  return (
    <div data-testid={testid} className="my-6 border-l-2 border-[#FF5500] bg-[#121418] p-5">
      {title && (
        <div className="font-mono-ind text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF5500]">
          {title}
        </div>
      )}
      <p className="mt-2 text-[15px] leading-relaxed text-slate-300">{text}</p>
    </div>
  );
}

export default function GuidePage() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  usePageTitle(
    guide ? guide.seoTitle : "Guide — Sitebound",
    guide?.description
  );

  if (!guide) {
    return (
      <div data-testid="guide-not-found" className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="font-display text-4xl font-extrabold uppercase text-slate-100">Guide not found</h1>
        <Link to="/guides" className="mt-6 inline-block text-[#FF5500] underline underline-offset-2">
          Back to all guides
        </Link>
      </div>
    );
  }

  const related = (guide.related || []).map(getGuide).filter(Boolean);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.h1,
    description: guide.description,
    datePublished: "2026-09-28",
    dateModified: "2026-09-28",
    inLanguage: "en-AU",
    mainEntityOfPage: `${CANON_BASE}/guides/${guide.slug}`,
    author: { "@type": "Organization", name: "Sitebound", url: CANON_BASE },
    publisher: {
      "@type": "Organization",
      name: "Sitebound",
      url: CANON_BASE,
      logo: { "@type": "ImageObject", url: `${CANON_BASE}/logo-full-dark.png` },
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${CANON_BASE}/` },
      { "@type": "ListItem", position: 2, name: "Guides", item: `${CANON_BASE}/guides` },
      { "@type": "ListItem", position: 3, name: guide.h1, item: `${CANON_BASE}/guides/${guide.slug}` },
    ],
  };

  return (
    <article data-testid={`guide-page-${guide.slug}`} className="mx-auto max-w-3xl px-4 py-14 sm:px-6 md:py-20">
      <JsonLd data={articleLd} />
      <JsonLd data={breadcrumbLd} />

      {/* breadcrumb */}
      <nav aria-label="Breadcrumb" className="font-mono-ind text-[11px] uppercase tracking-[0.2em] text-slate-500">
        <Link to="/" className="transition-colors hover:text-[#FF5500]">Home</Link>
        <span className="mx-2 text-slate-600">/</span>
        <Link to="/guides" className="transition-colors hover:text-[#FF5500]">Guides</Link>
        <span className="mx-2 text-slate-600">/</span>
        <span className="text-slate-400">{guide.category}</span>
      </nav>

      <Reveal>
        <header className="mt-8 border-b border-white/10 pb-10">
          <div className="mono-label">{guide.category}</div>
          <h1 className="font-display mt-4 text-3xl font-extrabold uppercase leading-[1.02] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            {guide.h1}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-400">{guide.intro}</p>
          <div className="font-mono-ind mt-6 flex flex-wrap gap-x-6 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-slate-500">
            <span data-testid="guide-last-reviewed">Last reviewed: {guide.lastReviewed}</span>
            <span>{guide.readTime}</span>
          </div>
        </header>
      </Reveal>

      {guide.calloutTop && <Callout title={guide.calloutTop.title} text={guide.calloutTop.text} testid="guide-callout-top" />}

      <div data-testid="guide-body" className="mt-10 space-y-12">
        {guide.sections.map((s, i) => (
          <Reveal key={s.h} delay={0.03}>
            <section>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-100">
                <span className="font-mono-ind mr-3 text-sm text-[#FF5500]">{String(i + 1).padStart(2, "0")}</span>
                {s.h}
              </h2>
              {s.body && s.body.map((p, j) => (
                <p key={j} className="mt-4 text-[15px] leading-relaxed text-slate-300">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2.5">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-300">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#FF5500]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.body2 && s.body2.map((p, j) => (
                <p key={j} className="mt-4 text-[15px] leading-relaxed text-slate-300">{p}</p>
              ))}
              {s.callout && <Callout title={s.callout.title} text={s.callout.text} />}
            </section>
          </Reveal>
        ))}
      </div>

      {guide.sources && (
        <section className="mt-14 border-t border-white/10 pt-8">
          <h2 className="font-mono-ind text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Sources &amp; where to verify
          </h2>
          <ul className="mt-4 space-y-2">
            {guide.sources.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 underline decoration-white/20 underline-offset-2 transition-colors hover:text-[#FF5500]"
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-relaxed text-slate-500">
            Requirements, vacancies and training details change. Always verify current requirements
            with the relevant regulator or employer before making decisions or payments.
          </p>
        </section>
      )}

      {/* restrained product CTA */}
      <section data-testid="guide-cta" className="mt-14 border border-[#FF5500]/30 bg-[#0D0F12] p-8">
        <div className="mono-label">From the Sitebound pack</div>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-300">{guide.cta}</p>
        <Link
          data-testid="guide-cta-link"
          to="/product/complete-construction-job-seeker-pack"
          className="mt-6 inline-flex items-center gap-2 bg-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
        >
          See the Complete Pack — $49 AUD <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </section>

      {related.length > 0 && (
        <section className="mt-14 border-t border-white/10 pt-8">
          <h2 className="font-mono-ind text-[11px] uppercase tracking-[0.25em] text-slate-500">Related guides</h2>
          <div className="mt-5 space-y-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/guides/${r.slug}`}
                data-testid={`related-guide-${r.slug}`}
                className="group flex items-center justify-between border border-white/10 px-5 py-4 transition-colors hover:border-[#FF5500]/50"
              >
                <span className="text-sm font-semibold text-slate-200 transition-colors group-hover:text-[#FF5500]">
                  {r.h1}
                </span>
                <ArrowRight className="h-4 w-4 text-slate-500 transition-colors group-hover:text-[#FF5500]" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-12">
        <Link
          to="/guides"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-[#FF5500]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All guides
        </Link>
      </div>
    </article>
  );
}
